import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { FacilityReportData } from "../../../models/FacilityReportData";
import DataSvc from "../../../services/dataSvc";

export type FacilityReportsSortFields = string;

export type FacilityReportsFilterFormData = {
  numberPerPage: number;
  pageIndex: number;
};

export type FacilityReportsSearchParams = {
  location_name?: string;
  sort: FacilityReportsSortFields;
  order: "asc" | "desc";
  limit: number;
  offset: number;
  seek_total_count: boolean;
  scores?: string[];
};

export const useFacilityReports = () => {
  const { t: _t } = useTranslation();
  const t = (key: string, args: any) =>
    _t(`facilityReportsPage.facilityReportsList.${key}`, args);

  const client = useQueryClient();

  const [searchParams, setSearchParams] = useState<FacilityReportsSearchParams>(
    {
      location_name: "",
      sort: "location.name",
      order: "asc",
      limit: 20,
      offset: 0,
      seek_total_count: true,
    }
  );

  // Filter Form Data is used for the legacy pagination component
  const filterFormData = useMemo(() => {
    const NUMBER_PER_PAGE = 20;
    return {
      numberPerPage: NUMBER_PER_PAGE,
      pageIndex: Math.floor(searchParams.offset / NUMBER_PER_PAGE) + 1,
    };
  }, [searchParams]);

  // handleChangeFilterFormData is used to change
  // the output from the legacy pagination component into search params
  const handleChangeFilterFormData = (
    filterFormData: FacilityReportsFilterFormData
  ) => {
    setSearchParams((sp) => {
      return {
        ...sp,
        offset: (filterFormData.pageIndex - 1) * filterFormData.numberPerPage,
      };
    });
  };

  const query = useQuery<{ data: FacilityReportData[]; count: number }>({
    queryKey: ["facilityReports", searchParams],
    queryFn: () => DataSvc.getFacilityReports(searchParams),
  });

  const clearAndRefetch = () => {
    client.invalidateQueries({ queryKey: ["facilityReports"] });
  };

  const createMutation = useMutation({
    mutationFn: (payload: any) => DataSvc.createFacilityReport(payload),
  });

  const create = async (payload: any) => {
    await createMutation.mutateAsync(payload);
    toast.success(
      t("report_created", { reportCount: payload.facility_ids.length })
    );
    clearAndRefetch();
  };

  return {
    query,
    filterFormData,
    handleChangeFilterFormData,
    searchParams,
    setSearchParams,
    totalCount: query.data?.count || 0,
    create,
  };
};
